/**
 * API client — all calls to the Azure Functions backend.
 * Passes the MSAL access token on every request so the backend
 * can identify the user (SWA Free tier doesn't inject client principal).
 */

import { msalInstance } from '../auth/AuthProvider';
import { loginScopes, dataverseScopes } from '../auth/msalConfig';

const API_BASE = '/api';

async function getToken(scopes: string[]): Promise<string> {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return '';
  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes,
      account: accounts[0],
    });
    return response.accessToken;
  } catch (err: any) {
    // Silent acquisition failed — need interactive consent (first time for this resource)
    if (err.name === 'InteractionRequiredAuthError' || err.errorCode === 'consent_required' || err.errorCode === 'interaction_required') {
      try {
        const response = await msalInstance.acquireTokenPopup({ scopes });
        return response.accessToken;
      } catch {
        // Popup blocked or failed — fall back to redirect
        await msalInstance.acquireTokenRedirect({ scopes });
        return ''; // won't reach here
      }
    }
    console.error('Token acquisition failed for scopes:', scopes, err);
    return '';
  }
}

async function fetchApi(path: string, options: RequestInit = {}): Promise<any> {
  // Get both tokens — Graph for user identity, Dataverse for CRM data
  const [graphToken, dvToken] = await Promise.all([
    getToken(loginScopes),
    getToken(dataverseScopes),
  ]);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  if (graphToken) {
    headers['Authorization'] = `Bearer ${graphToken}`;
  }
  if (dvToken) {
    headers['x-dataverse-token'] = dvToken;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `API error: ${res.status}`);
  }

  return res.json();
}

// ── Me ──
export async function getMe() {
  return fetchApi('/me');
}

// ── Sync ──
export async function syncUsers() {
  return fetchApi('/sync-users', { method: 'POST' });
}

// ── Settings: Users ──
export async function getSettingsUsers(territory?: string) {
  const query = territory ? `?territory=${encodeURIComponent(territory)}` : '';
  return fetchApi(`/settings/users${query}`);
}

export async function updateSettingsUser(id: string, updates: Record<string, any>) {
  return fetchApi(`/settings/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

// ── Settings: Teams ──
export async function getSettingsTeams(territory?: string) {
  const query = territory ? `?territory=${encodeURIComponent(territory)}` : '';
  return fetchApi(`/settings/teams${query}`);
}

export async function createSettingsTeam(data: { name: string; territory: string; teamLeadId?: string; teamLeadName?: string }) {
  return fetchApi('/settings/teams', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSettingsTeam(id: string, updates: Record<string, any>) {
  return fetchApi(`/settings/teams/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function deleteSettingsTeam(id: string) {
  return fetchApi(`/settings/teams/${id}`, { method: 'DELETE' });
}

// ── Settings: Territories ──
export async function getSettingsTerritories() {
  return fetchApi('/settings/territories');
}

export async function updateSettingsTerritory(name: string, updates: Record<string, any>) {
  return fetchApi(`/settings/territories/${encodeURIComponent(name)}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

// ── Settings: Close Reasons ──
export async function getCloseReasons() {
  return fetchApi('/settings/close-reasons');
}

export async function updateCloseReason(value: number, visible: boolean) {
  return fetchApi(`/settings/close-reasons/${value}`, {
    method: 'PATCH',
    body: JSON.stringify({ visible }),
  });
}

// ── Score ──
export async function getScore(userId?: string, month?: string) {
  const parts = [userId, month].filter(Boolean).join('/');
  const path = parts ? `/score/${parts}` : '/score';
  return fetchApi(path);
}

// ── Worklist ──
export async function getWorklist(userId?: string) {
  const path = userId ? `/worklist/${userId}` : '/worklist';
  return fetchApi(path);
}

// ── Team scores ──
export async function getTeamScores(teamId: string) {
  return fetchApi(`/team-scores/${teamId}`);
}

// ── Territory scores ──
export async function getTerritoryScores(territory: string) {
  return fetchApi(`/territory-scores/${encodeURIComponent(territory)}`);
}

// ── Actions ──
export async function actionLogCall(candidateId: string, candidateName: string, description?: string, onBehalfOfUserId?: string) {
  return fetchApi('/action/log-call', { method: 'POST', body: JSON.stringify({ candidateId, candidateName, description, onBehalfOfUserId }) });
}

export async function actionCloseVacancy(vacancyId: string, statusCode: number, recordName?: string) {
  return fetchApi('/action/close-vacancy', { method: 'POST', body: JSON.stringify({ vacancyId, statusCode, recordName }) });
}

export async function actionFixCurrency(vacancyId: string, currencyId: string, recordName?: string) {
  return fetchApi('/action/fix-currency', { method: 'POST', body: JSON.stringify({ vacancyId, currencyId, recordName }) });
}

export async function actionFixAddress(vacancyId: string, countryId: string, recordName?: string) {
  return fetchApi('/action/fix-address', { method: 'POST', body: JSON.stringify({ vacancyId, countryId, recordName }) });
}

export async function actionFixSolution(vacancyId: string, solutionId: string, recordName?: string) {
  return fetchApi('/action/fix-solution', { method: 'POST', body: JSON.stringify({ vacancyId, solutionId, recordName }) });
}

export async function actionUpdateContact(candidateId: string, email?: string, phone?: string, firstName?: string, lastName?: string, recordName?: string) {
  return fetchApi('/action/update-contact', { method: 'POST', body: JSON.stringify({ candidateId, email, phone, firstName, lastName, recordName }) });
}

export async function actionAddTags(candidateId: string, sourceId?: string, sectorName?: string, city?: string, countryId?: string, recordName?: string) {
  return fetchApi('/action/add-tags', { method: 'POST', body: JSON.stringify({ candidateId, sourceId, sectorName, city, countryId, recordName }) });
}

export async function actionAcknowledge(consultantId: string, recordId: string, checkType: string) {
  return fetchApi('/action/acknowledge', { method: 'POST', body: JSON.stringify({ consultantId, recordId, checkType }) });
}

export async function actionDismiss(consultantId: string, recordId: string, checkType: string, reason?: string) {
  return fetchApi('/action/dismiss', { method: 'POST', body: JSON.stringify({ consultantId, recordId, checkType, reason }) });
}

export async function actionUploadCv(fileName: string, fileContentBase64: string, graphToken: string, candidateId?: string) {
  return fetchApi('/action/upload-cv', {
    method: 'POST',
    headers: { 'x-graph-token': graphToken },
    body: JSON.stringify({ fileName, fileContentBase64, candidateId }),
  });
}

// ── Lookups ──
export async function getLookupCurrencies() { return fetchApi('/lookup/currencies'); }
export async function getLookupCountries() { return fetchApi('/lookup/countries'); }
// ── Admin: Notifications ──
export async function sendScoreAlerts(opts: { threshold?: number; includeUnassigned?: boolean; dryRun?: boolean }, graphToken: string) {
  return fetchApi('/send-score-alerts', {
    method: 'POST',
    headers: { 'x-graph-token': graphToken },
    body: JSON.stringify(opts),
  });
}

export async function sendSummary(graphToken: string) {
  return fetchApi('/send-summary', {
    method: 'POST',
    headers: { 'x-graph-token': graphToken },
    body: JSON.stringify({}),
  });
}


export async function getLookupSources() { return fetchApi('/lookup/sources'); }
export async function getLookupSectors() { return fetchApi('/lookup/sectors'); }
export async function getLookupCloseReasons() { return fetchApi('/lookup/close-reasons'); }

// ── Org snapshot ──
export async function getOrgSnapshot(refresh = false) {
  return fetchApi(`/org-snapshot${refresh ? '?refresh=1' : ''}`);
}

// ── Company trend ──
export async function getCompanyTrend(days = 180) {
  return fetchApi(`/company-trend?days=${days}`);
}

// ── CV duplicates ──
export async function getCvDuplicates() {
  return fetchApi('/cv-duplicates');
}

export async function resolveCvDuplicate(id: string, resolved = true) {
  return fetchApi('/cv-duplicates/resolve', { method: 'POST', body: JSON.stringify({ id, resolved }) });
}

// ── Audit log ──
export async function getAuditLog(days: number = 30) {
  return fetchApi(`/audit-log?days=${days}`);
}
